import React from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin } from "lucide-react";

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Header */}
      <header className="bg-white border-b border-[#F4F1EA] py-6">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-[#E07A5F] rounded-full flex items-center justify-center">
              <Phone className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-[#3D405B]" style={{ fontFamily: 'Fraunces, serif' }}>
              OuttaTouch
            </span>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        <h1 className="text-4xl font-bold text-[#3D405B] mb-2" style={{ fontFamily: 'Fraunces, serif' }}>
          Terms of Service
        </h1>
        <p className="text-[#6D6F7C] mb-8">Last updated: January 2025</p>

        <div className="prose prose-lg max-w-none text-[#3D405B]">
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#3D405B] mb-4" style={{ fontFamily: 'Fraunces, serif' }}>
              1. Agreement to Terms
            </h2>
            <p className="mb-4">
              By accessing or using OuttaTouch ("the Service"), operated by <strong>MidVantage Labs</strong> ("Company", "we", "us", or "our"), 
              you agree to be bound by these Terms of Service ("Terms"). If you disagree with any part of these terms, 
              you may not access the Service.
            </p>
            <p>
              The Service is available at <a href="https://tryouttatouch.com" className="text-[#E07A5F] hover:underline">tryouttatouch.com</a> and 
              through our mobile applications.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#3D405B] mb-4" style={{ fontFamily: 'Fraunces, serif' }}>
              2. Description of Service
            </h2>
            <p className="mb-4">
              OuttaTouch is a relationship management application designed to help users maintain meaningful connections 
              with friends and family through:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Contact management and call tracking</li>
              <li>Reminder notifications for staying in touch</li>
              <li>Streak tracking to maintain connection habits</li>
              <li>AI-powered call summaries (Plus and Family plans)</li>
              <li>Event and occasion tracking (birthdays, anniversaries)</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#3D405B] mb-4" style={{ fontFamily: 'Fraunces, serif' }}>
              3. User Accounts
            </h2>
            <p className="mb-4">
              To use certain features of the Service, you must register for an account. You agree to:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Provide accurate, current, and complete information during registration</li>
              <li>Maintain the security of your password and account</li>
              <li>Accept responsibility for all activities that occur under your account</li>
              <li>Notify us immediately of any unauthorized use of your account</li>
            </ul>
            <p>
              You must be at least 13 years old to use the Service. If you are under 18, you must have parental consent.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#3D405B] mb-4" style={{ fontFamily: 'Fraunces, serif' }}>
              4. Subscription and Payments
            </h2>
            <p className="mb-4">
              OuttaTouch offers free and paid subscription tiers:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li><strong>Free:</strong> Basic features with up to 10 contacts</li>
              <li><strong>Plus ($4.99/month):</strong> Unlimited contacts, AI call summaries, priority reminders</li>
              <li><strong>Family ($11.99/month):</strong> All Plus features for up to 5 individual family accounts under one subscription</li>
            </ul>
            
            <p className="text-sm text-[#6D6F7C] mb-4">
              Note: Family plan members have separate, private accounts. Contacts, calls, and reminders are not shared between family members.
            </p>
            
            <div className="bg-[#F4F1EA] rounded-2xl p-6 mb-4">
              <h4 className="font-bold text-[#3D405B] mb-2">Payment Processing via Stripe</h4>
              <p className="text-sm text-[#6D6F7C]">
                All payment transactions are securely processed by <strong>Stripe, Inc.</strong>, a PCI-DSS compliant payment processor. 
                Your payment information is transmitted directly to Stripe and is never stored on our servers. 
                By subscribing, you also agree to Stripe's <a href="https://stripe.com/legal" className="text-[#E07A5F] hover:underline" target="_blank" rel="noopener noreferrer">Terms of Service</a>.
              </p>
            </div>
            
            <p className="mb-4">
              <strong>Billing:</strong> Subscriptions are billed monthly or annually. Your subscription will automatically renew 
              at the end of each billing period unless you cancel before the renewal date.
            </p>
            <p className="mb-4">
              <strong>Cancellation:</strong> You may cancel your subscription at any time through your account settings. 
              Upon cancellation, you will retain access to paid features until the end of your current billing period.
            </p>
            <p>
              <strong>Refunds:</strong> Subscription fees are non-refundable except where required by law. 
              Gift purchases are non-refundable once the gift has been sent or delivered.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#3D405B] mb-4" style={{ fontFamily: 'Fraunces, serif' }}>
              5. Contact Data Responsibility
            </h2>
            <div className="bg-[#E07A5F]/10 rounded-2xl p-6 mb-4 border border-[#E07A5F]/30">
              <h4 className="font-bold text-[#3D405B] mb-2">Important: Your Responsibility</h4>
              <p className="text-[#3D405B]">
                You are solely responsible for ensuring you have the appropriate consent and legal basis to store 
                contact information (names, phone numbers, email addresses, birthdays, etc.) of other individuals in OuttaTouch.
              </p>
            </div>
            <p className="mb-4">
              By using OuttaTouch to store information about your contacts, you represent and warrant that:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>You have obtained necessary consent from individuals whose data you input into the Service</li>
              <li>The information you provide is accurate and you have the right to share it</li>
              <li>You will not use the Service to store data in violation of any privacy laws</li>
              <li>You understand that imported contacts (e.g., from Google) remain your responsibility</li>
            </ul>
            <p>
              MidVantage Labs is not responsible for any claims arising from your storage or use of third-party contact information.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#3D405B] mb-4" style={{ fontFamily: 'Fraunces, serif' }}>
              6. AI Features Disclaimer
            </h2>
            <div className="bg-[#81B29A]/10 rounded-2xl p-6 mb-4 border border-[#81B29A]/30">
              <h4 className="font-bold text-[#3D405B] mb-2">AI-Generated Content Notice</h4>
              <p className="text-[#3D405B]">
                OuttaTouch uses artificial intelligence (AI) to provide features such as message suggestions, 
                call preparation talking points, post-call summaries, and gift recommendations.
              </p>
            </div>
            <p className="mb-4">
              <strong>You acknowledge and agree that:</strong>
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>AI-generated suggestions are provided "as-is" and may not always be accurate, appropriate, or suitable for your specific situation</li>
              <li>You are solely responsible for reviewing and editing any AI-generated content before use</li>
              <li>AI features are intended as assistive tools, not replacements for personal judgment</li>
              <li>We do not guarantee the accuracy, completeness, or appropriateness of AI-generated content</li>
              <li>AI features may be updated, modified, or discontinued at any time</li>
              <li>Data you provide may be processed by third-party AI services (see Privacy Policy for details)</li>
            </ul>
            <p>
              MidVantage Labs is not liable for any consequences arising from your use of AI-generated suggestions.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#3D405B] mb-4" style={{ fontFamily: 'Fraunces, serif' }}>
              7. Gift Services
            </h2>
            <p className="mb-4">
              OuttaTouch facilitates the purchase and delivery of digital and physical gifts through third-party providers, 
              including but not limited to:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li><strong>Digital gift cards</strong> via Tremendous</li>
              <li><strong>Handwritten notes</strong> via Handwrytten</li>
              <li><strong>Flower delivery</strong> via Florist One or similar providers</li>
            </ul>
            <p className="mb-4">
              We are not responsible for:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Delays in gift delivery caused by third-party providers</li>
              <li>Quality issues with physical products</li>
              <li>Gift card redemption issues handled by the card issuer</li>
              <li>Incorrect delivery addresses provided by you</li>
            </ul>
            <p>
              For issues with gifts, please contact <a href="mailto:support@tryouttatouch.com" className="text-[#E07A5F] hover:underline">support@tryouttatouch.com</a>, 
              and we will assist in resolving the matter with the appropriate provider.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#3D405B] mb-4" style={{ fontFamily: 'Fraunces, serif' }}>
              8. Acceptable Use
            </h2>
            <p className="mb-4">You agree not to:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Use the Service for any unlawful purpose or in violation of these Terms</li>
              <li>Harass, abuse, stalk, or harm other individuals using information from the Service</li>
              <li>Attempt to gain unauthorized access to our systems or other users' accounts</li>
              <li>Use automated systems, bots, or scrapers to access the Service without permission</li>
              <li>Upload malicious code, viruses, or interfere with the Service's operation</li>
              <li>Use the Service to send spam, unsolicited communications, or marketing materials</li>
              <li>Impersonate any person or entity or misrepresent your affiliation</li>
              <li>Violate any applicable local, state, national, or international laws or regulations</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#3D405B] mb-4" style={{ fontFamily: 'Fraunces, serif' }}>
              9. Intellectual Property
            </h2>
            <p className="mb-4">
              The Service and its original content, features, and functionality are owned by MidVantage Labs 
              and are protected by international copyright, trademark, and other intellectual property laws.
            </p>
            <p>
              You retain ownership of any content you submit to the Service (notes, contact information, etc.). 
              By submitting content, you grant us a non-exclusive, worldwide, royalty-free license to use, store, 
              and process that content solely for the purpose of providing and improving the Service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#3D405B] mb-4" style={{ fontFamily: 'Fraunces, serif' }}>
              10. Third-Party Services
            </h2>
            <p className="mb-4">
              The Service integrates with and relies upon various third-party services. Your use of these integrations 
              is subject to the respective third party's terms and privacy policies:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li><strong>Google:</strong> For contact import and authentication</li>
              <li><strong>Stripe:</strong> For payment processing</li>
              <li><strong>OpenAI:</strong> For AI-powered features</li>
              <li><strong>SendGrid:</strong> For email notifications</li>
              <li><strong>Gift providers:</strong> For gift fulfillment</li>
            </ul>
            <p>
              We are not responsible for the practices of these third-party services.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#3D405B] mb-4" style={{ fontFamily: 'Fraunces, serif' }}>
              11. Limitation of Liability
            </h2>
            <p className="mb-4">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, MIDVANTAGE LABS SHALL NOT BE LIABLE FOR ANY INDIRECT, 
              INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Loss of profits, data, or goodwill</li>
              <li>Service interruption or inability to use the Service</li>
              <li>Damages arising from AI-generated content</li>
              <li>Issues with third-party gift delivery</li>
              <li>Unauthorized access to your account</li>
            </ul>
            <p>
              Our total liability for any claims under these Terms shall not exceed the amount you paid us 
              in the twelve (12) months preceding the claim, or $100 USD, whichever is greater.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#3D405B] mb-4" style={{ fontFamily: 'Fraunces, serif' }}>
              12. Disclaimer of Warranties
            </h2>
            <p className="mb-4">
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, 
              EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF 
              MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
            </p>
            <p>
              We do not warrant that the Service will be uninterrupted, secure, or error-free, 
              or that any defects will be corrected.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#3D405B] mb-4" style={{ fontFamily: 'Fraunces, serif' }}>
              13. Termination
            </h2>
            <p className="mb-4">
              We may terminate or suspend your account and access to the Service immediately, without prior notice, 
              for conduct that we believe:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Violates these Terms</li>
              <li>Is harmful to other users, us, or third parties</li>
              <li>Constitutes fraud or illegal activity</li>
            </ul>
            <p>
              Upon termination, your right to use the Service will cease immediately. You may request 
              deletion of your account data by contacting <a href="mailto:support@tryouttatouch.com" className="text-[#E07A5F] hover:underline">support@tryouttatouch.com</a>.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#3D405B] mb-4" style={{ fontFamily: 'Fraunces, serif' }}>
              14. Changes to Terms
            </h2>
            <p>
              We reserve the right to modify these Terms at any time. We will notify users of any material changes 
              by posting the new Terms on this page and updating the "Last updated" date. For significant changes, 
              we may also notify you via email or in-app notification. Your continued use of the Service after such 
              modifications constitutes acceptance of the updated Terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#3D405B] mb-4" style={{ fontFamily: 'Fraunces, serif' }}>
              15. Governing Law
            </h2>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of the United States, 
              without regard to its conflict of law provisions. Any disputes arising under these Terms shall be 
              resolved in the courts of the jurisdiction where MidVantage Labs is headquartered.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#3D405B] mb-4" style={{ fontFamily: 'Fraunces, serif' }}>
              16. Contact Us
            </h2>
            <p className="mb-4">
              If you have any questions about these Terms, please contact us:
            </p>
            <div className="bg-[#F4F1EA] rounded-2xl p-6">
              <p className="font-bold text-[#3D405B] mb-2">MidVantage Labs</p>
              <div className="space-y-2 text-[#6D6F7C]">
                <p className="flex items-center gap-2">
                  <Mail className="w-4 h-4" /> support@tryouttatouch.com
                </p>
                <p className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" /> tryouttatouch.com
                </p>
              </div>
            </div>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-[#F4F1EA] py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center text-[#6D6F7C]">
          <p>© 2025 MidVantage Labs. All rights reserved.</p>
          <div className="flex justify-center gap-4 mt-4">
            <Link to="/terms" className="hover:text-[#E07A5F]">Terms of Service</Link>
            <Link to="/privacy" className="hover:text-[#E07A5F]">Privacy Policy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfService;
